/**
 * Email-verification landing page — `/verify-email/:token`
 *
 * Calls GET /api/auth/verify-email/:token which:
 *   - marks the user verified
 *   - auto-claims any Pro scans matching the user's email
 *   - issues a session cookie
 *
 * Then redirects into the dashboard. Distinct from `/verify/:token`, which
 * is the older magic-link landing for Pro buyers (no user account).
 */
import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { CheckCircle2, Loader2, AlertCircle } from "lucide-react";

import { apiClient } from "../lib/api";
import { useAuth } from "../lib/auth";

export default function VerifyAccount() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [state, setState] = useState({ status: "checking", error: "", linked: 0 });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await apiClient.get(`/auth/verify-email/${token}`, {
          withCredentials: true,
        });
        if (cancelled) return;
        await refresh();
        setState({
          status: "ok",
          error: "",
          linked: r.data?.linked_scans || 0,
        });
        // Auto-redirect after 2.5s
        setTimeout(() => {
          if (!cancelled) navigate("/dashboard", { replace: true });
        }, 2500);
      } catch (ex) {
        if (cancelled) return;
        const detail =
          ex?.response?.data?.detail ||
          "Verification failed. Try requesting a new link.";
        setState({ status: "error", error: detail, linked: 0 });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token, refresh, navigate]);

  return (
    <main className="border-b border-black min-h-[80vh]" data-testid="verify-email-page">
      <section className="max-w-md mx-auto px-6 lg:px-8 py-16 text-center">
        {state.status === "checking" && (
          <>
            <Loader2 className="w-10 h-10 animate-spin text-[#0047FF] mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-tight mb-2">
              Verifying your email…
            </h1>
            <p className="text-[#475569] text-sm">Just a moment.</p>
          </>
        )}

        {state.status === "ok" && (
          <>
            <CheckCircle2 className="w-12 h-12 text-[#10B981] mx-auto mb-4" />
            <h1
              className="font-display text-3xl tracking-tight mb-3"
              data-testid="verify-email-success"
            >
              You&apos;re verified.
            </h1>
            <p className="text-[#475569] text-sm mb-4 leading-relaxed">
              Welcome to PrivacyLens. Redirecting you to the dashboard…
            </p>
            {state.linked > 0 && (
              <p className="text-sm text-[#0A0A0A] bg-[#ECFDF5] border border-[#10B981] px-3 py-2 mb-4">
                {state.linked === 1
                  ? "1 Pro purchase was linked to your new account."
                  : `${state.linked} Pro purchases were linked to your new account.`}
              </p>
            )}
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-black bg-white text-sm hover:bg-[#F1F5F9]"
            >
              Open dashboard now →
            </Link>
          </>
        )}

        {state.status === "error" && (
          <>
            <AlertCircle className="w-12 h-12 text-[#F59E0B] mx-auto mb-4" />
            <h1 className="font-display text-3xl tracking-tight mb-3">
              Verification failed.
            </h1>
            <p
              className="text-[#B91C1C] bg-[#FEE2E2] border border-[#FCA5A5] px-3 py-2 text-sm mb-4"
              data-testid="verify-email-error"
            >
              {state.error}
            </p>
            <Link
              to="/login"
              className="inline-flex items-center gap-2 px-5 py-2.5 border border-black bg-white text-sm hover:bg-[#F1F5F9]"
            >
              Back to login
            </Link>
          </>
        )}
      </section>
    </main>
  );
}
